import {
  create,
  maxDependencies,
  bignumberDependencies,
  roundDependencies,
  multiplyDependencies,
  divideDependencies,
} from 'mathjs'
import Decimal from 'decimal.js'
import _ from 'lodash'

const { max, bignumber, round, multiply, divide } = create({
  maxDependencies,
  bignumberDependencies,
  roundDependencies,
  multiplyDependencies,
  divideDependencies,
})

export default {
  methods: {
    chartNumberFormatter(value) {
      return parseFloat(parseFloat(value).toFixed(2))
    },
    /**
     * Finds max number of decimal places required for given numbers
     */
    _maxDecimalPlaces(number) {
      let maxPlaces = 0
      if (this.isFloat(number)) {
        // assume 16 as the maximum precision
        const valueToCheck = parseFloat(number.toFixed(16))
        const decimalPlaces = this._decimalPlaces(valueToCheck)
        maxPlaces = max(maxPlaces, decimalPlaces)
      }
      return maxPlaces
    },

    /**
     * Finds number of decimal places of a given number
     */
    _decimalPlaces(value) {
      let powerOf10 = 1
      let decimalPlaces = 0
      while (!this._isCorrectPrecision(value, powerOf10)) {
        powerOf10 *= 10
        decimalPlaces += 1
      }
      return decimalPlaces
    },

    /**
     * Checks if a given power of 10 matches the precision of a value
     */
    _isCorrectPrecision(value, powerOf10) {
      const valueBigNumber = bignumber(value)
      const powerOf10BigNumber = bignumber(powerOf10)
      const roundedValue = round(multiply(valueBigNumber, powerOf10BigNumber))
      return divide(roundedValue, powerOf10BigNumber).toNumber() === value
    },

    /**
     * Validate whether the passed value is a numeric float value
     */
    isFloat(value) {
      return !Number.isNaN(parseFloat(value)) && Number.isFinite(value)
    },

    /**
     * Returns true if input value precision is kept after saving
     */
    isPrecisionKeptAfterSaving(inputValue) {
      const number = bignumber(inputValue)
      return number.toNumber().toString() === number.toString()
    },
    /**
     * Parses the given Number string using system's locale
     * @param value to parse
     * @returns {string|undefined} parsed Number String, undefined if parsing was unsuccessful
     */
    getGlobalizedInputValue(value) {
      if (!value) {
        return undefined
      }
      return this.parseNumberString(value.toString(), this.$profile.locale).toString()
    },
    /**
     * Parses a given javascript Number to a string using system's locale
     * @param value Javascript Number to parse
     * @param useGrouping flag indicating whether resulting number String should contain
     *                    group separators
     * @returns {string} Number string formatted according to the system locale
     */
    getLocalizedInputValue(value, useGrouping = false) {
      const parsedValue = parseFloat(value)
      const decimalPlaces = this._maxDecimalPlaces(parsedValue)

      return parsedValue.toLocaleString(this.$profile.locale, {
        useGrouping,
        minimumFractionDigits: decimalPlaces,
        maximumFractionDigits: decimalPlaces,
      })
    },
    /**
     * Checks if the passed value (Number or String) is a decimal number
     * @param valueToCheck Number or String to check
     */
    isDecimal(valueToCheck) {
      try {
        const decimal = new Decimal(valueToCheck)
        return decimal.toNumber().toString().length === valueToCheck.toString().length
      } catch (e) {
        return false
      }
    },
    /**
     * Parses a localised number string and returns a Number, uses supplied locale
     * to correctly parse localised number string, returns false in case number contained illegal
     * symbols.
     * For Example, given a string of '123,45' (comma separated decimal point) and locale de-DE
     * Number will be parsed correctly
     * Inspired by https://observablehq.com/@mbostock/localized-number-parsing
     */
    parseNumberString(value, locale) {
      if (value && _.isString(value)) {
        const parts = new Intl.NumberFormat(locale).formatToParts(12345.6)
        const numerals = [
          ...new Intl.NumberFormat(locale, { useGrouping: false }).format(9876543210),
        ].reverse()
        const index = new Map(numerals.map((d, i) => [d, i]))
        this._decimal = new RegExp(`[${parts.find((d) => d.type === 'decimal').value}]`)
        this._numeral = new RegExp(`[${numerals.join('')}]`, 'g')
        this._index = (d) => index.get(d)
        let parsedValue = value
          .trim()
          .replace(this._decimal, '.')
          .replace(this._numeral, this._index)
        try {
          if (
            parsedValue.length !== value.length ||
            !this.isPrecisionKeptAfterSaving(parsedValue)
          ) {
            return false
          }
        } catch (e) {
          return false
        }
        parsedValue = +parsedValue
        return parsedValue
      }
      return false
    },
    getSeparatorsForLocale(locale) {
      const parts = new Intl.NumberFormat(locale).formatToParts(12345.6)
      const group = parts.find((d) => d.type === 'group').value
      const decimal = parts.find((d) => d.type === 'decimal').value

      return { group, decimal }
    },
    getCleaveOptions(locale, valueChangedCallback) {
      const separators = this.getSeparatorsForLocale(locale)
      return {
        numeral: true,
        numeralDecimalMark: separators.decimal,
        delimiter: separators.group,
        numeralDecimalScale: 16,
        swapHiddenInput: true,
        onValueChanged: valueChangedCallback.bind(this),
      }
    },
  },
}
